import React, { useEffect, useMemo, useState } from "react";
import { api, API, tokenStore } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Receipt, Building2, Calendar, Users, FileText, RotateCcw, CheckCircle2 } from "lucide-react";
import { DropZone } from "../components/DropZone";

/**
 * 📑 Facturare — checklist săptămânal admin
 *
 * - Vede toate proiectele "Demontat de Facturat" (cat=7) + regiile cu zile lucrate în săptămâni precedente nefacturate.
 * - Click „Facturează" → dialog cu detalii complete + note "în plus" / "în minus" + confirmare.
 * - După confirmare: proiect → cat=8, săptămână regie → flagged invoiced.
 * - Tabs: 🔴 De facturat / ✅ Facturate (istoric persistent).
 */
export default function Facturare() {
  const [tab, setTab] = useState("pending"); // pending | invoiced
  const [section, setSection] = useState("projects"); // projects | regii
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { kind: "project"|"regie", id, week_start?, details }
  const [notesPlus, setNotesPlus] = useState("");
  const [notesMinus, setNotesMinus] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/todo?include_invoiced=true");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcare");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openInvoiceDialog = async (kind, item) => {
    setBusy(true);
    try {
      let url;
      if (kind === "project") url = `/billing/details/project/${item.id}`;
      else url = `/billing/details/regie/${item.regie_id}/week/${item.week_start}`;
      const { data: det } = await api.get(url);
      setDialog({ kind, item, details: det });
      setNotesPlus("");
      setNotesMinus("");
    } catch (e) {
      toast.error("Eroare la încărcare detalii");
    } finally { setBusy(false); }
  };

  const confirmInvoice = async () => {
    if (!dialog) return;
    setBusy(true);
    try {
      const body = { notes_plus: notesPlus, notes_minus: notesMinus };
      if (dialog.kind === "project") {
        await api.post(`/billing/invoice/project/${dialog.item.id}`, body);
      } else {
        await api.post(`/billing/invoice/regie/${dialog.item.regie_id}/week/${dialog.item.week_start}`, body);
      }
      toast.success("✅ Marcat ca facturat");
      setDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally { setBusy(false); }
  };

  const unmark = async (kind, item) => {
    if (!window.confirm(`Sigur anulezi facturarea?\n\nElementul revine în lista „De facturat".`)) return;
    try {
      if (kind === "project") await api.post(`/billing/unmark/project/${item.id}`);
      else await api.post(`/billing/unmark/regie/${item.regie_id}/week/${item.week_start}`);
      toast.success("Facturare anulată");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const items = useMemo(() => {
    if (!data) return { projects: [], regii: [] };
    if (tab === "pending") return { projects: data.projects_pending, regii: data.regii_pending };
    return { projects: data.projects_invoiced, regii: data.regii_invoiced };
  }, [data, tab]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
    } catch { return iso; }
  };
  const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return `${fmtDate(iso)} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    } catch { return iso; }
  };

  const s = data?.summary || {};
  return (
    <div className="p-2" data-testid="page-facturare">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold flex items-center gap-2">
            <Receipt size={28}/>Facturare
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Checklist săptămânal pentru proiecte demontate și regii cu zile lucrate în săptămânile precedente.
            Bifează când ai facturat — elementele rămân vizibile în tab-ul „Facturate".
          </p>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <div className="card-flat p-3 bg-red-50 border-red-200 border-2">
          <div className="text-[10px] uppercase font-bold tracking-wider text-red-700">Proiecte de facturat</div>
          <div className="text-3xl font-display font-extrabold text-red-900 tabular-nums">{s.projects_pending || 0}</div>
        </div>
        <div className="card-flat p-3 bg-amber-50 border-amber-200 border-2">
          <div className="text-[10px] uppercase font-bold tracking-wider text-amber-700">Regii de facturat</div>
          <div className="text-3xl font-display font-extrabold text-amber-900 tabular-nums">{s.regii_pending || 0}</div>
        </div>
        <div className="card-flat p-3 bg-emerald-50 border-emerald-200 border-2">
          <div className="text-[10px] uppercase font-bold tracking-wider text-emerald-700">Proiecte facturate</div>
          <div className="text-3xl font-display font-extrabold text-emerald-900 tabular-nums">{s.projects_invoiced || 0}</div>
        </div>
        <div className="card-flat p-3 bg-emerald-50 border-emerald-200 border-2">
          <div className="text-[10px] uppercase font-bold tracking-wider text-emerald-700">Săptămâni regii facturate</div>
          <div className="text-3xl font-display font-extrabold text-emerald-900 tabular-nums">{s.regii_invoiced || 0}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-3 border-b border-zinc-200">
        {[
          { k: "pending", label: `🔴 De facturat (${(s.projects_pending || 0) + (s.regii_pending || 0)})` },
          { k: "invoiced", label: `✅ Facturate (${(s.projects_invoiced || 0) + (s.regii_invoiced || 0)})` },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-bold transition-colors border-b-2 ${tab === t.k ? "border-[var(--primary)] text-[var(--primary)]" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}
            data-testid={`tab-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm text-zinc-500 italic">Se încarcă…</div>
      ) : (
        <div className="space-y-4">
          {/* Selector secțiune: Proiecte / Regii */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSection("projects")}
              className={`px-4 py-2 rounded-lg text-sm font-bold border-2 transition-colors inline-flex items-center gap-1.5 ${section === "projects" ? "bg-red-600 text-white border-red-600" : "bg-white text-zinc-700 border-zinc-300 hover:border-red-400"}`}
              data-testid="section-btn-projects"
            >
              <Building2 size={15}/> Proiecte ({items.projects.length})
            </button>
            <button
              onClick={() => setSection("regii")}
              className={`px-4 py-2 rounded-lg text-sm font-bold border-2 transition-colors inline-flex items-center gap-1.5 ${section === "regii" ? "bg-amber-600 text-white border-amber-600" : "bg-white text-zinc-700 border-zinc-300 hover:border-amber-400"}`}
              data-testid="section-btn-regii"
            >
              <Calendar size={15}/> Regii ({items.regii.length})
            </button>
          </div>
          {/* PROIECTE */}
          {section === "projects" && items.projects.length > 0 && (
            <section>
              <h2 className="font-display font-bold text-base mb-2 flex items-center gap-2">
                <Building2 size={16}/>Șantiere · Proiecte ({items.projects.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {items.projects.map((p) => (
                  <BillingCard
                    key={p.id} kind="project" item={p} tab={tab}
                    onInvoice={() => openInvoiceDialog("project", p)}
                    onUnmark={() => unmark("project", p)}
                    onOpenDetails={openInvoiceDialog}
                    fmtDateTime={fmtDateTime}
                  />
                ))}
              </div>
            </section>
          )}
          {/* REGII */}
          {section === "regii" && items.regii.length > 0 && (
            <section>
              <h2 className="font-display font-bold text-base mb-2 flex items-center gap-2">
                <Calendar size={16}/>Regii pe săptămâni ({items.regii.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {items.regii.map((r) => (
                  <BillingCard
                    key={r.id} kind="regie" item={r} tab={tab}
                    onInvoice={() => openInvoiceDialog("regie", r)}
                    onUnmark={() => unmark("regie", r)}
                    onOpenDetails={openInvoiceDialog}
                    fmtDateTime={fmtDateTime}
                  />
                ))}
              </div>
            </section>
          )}
          {((section === "projects" && items.projects.length === 0) || (section === "regii" && items.regii.length === 0)) && (
            <div className="p-8 text-center text-sm text-zinc-500 italic" data-testid="empty-state">
              {tab === "pending"
                ? (section === "projects" ? "🎉 Niciun proiect de facturat!" : "🎉 Nicio regie de facturat!")
                : "Niciun element facturat încă în această secțiune."}
            </div>
          )}
        </div>
      )}

      {/* Dialog Facturează */}
      <Dialog open={!!dialog} onOpenChange={(v) => { if (!v && !busy) setDialog(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt size={18}/>Confirmă facturarea
            </DialogTitle>
          </DialogHeader>
          {dialog && <InvoiceDetailsBlock dialog={dialog} fmtDate={fmtDate}/>}
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-emerald-700 font-bold">➕ În PLUS față de ofertă</Label>
              <Textarea
                rows={2}
                value={notesPlus} onChange={(e) => setNotesPlus(e.target.value)}
                placeholder="Ex: schimbat 2 măști extra, lucrat 1h peste program…"
                data-testid="notes-plus"
                className="border-emerald-300 focus-visible:ring-emerald-500"
              />
            </div>
            <div>
              <Label className="text-red-700 font-bold">➖ În MINUS față de ofertă</Label>
              <Textarea
                rows={2}
                value={notesMinus} onChange={(e) => setNotesMinus(e.target.value)}
                placeholder="Ex: o zonă nu s-a putut face din lipsă acces…"
                data-testid="notes-minus"
                className="border-red-300 focus-visible:ring-red-500"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} disabled={busy}>Anulează</Button>
            <Button onClick={confirmInvoice} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-invoice-btn">
              <CheckCircle2 size={14} className="mr-1.5"/>
              {busy ? "Se salvează…" : "Confirmă FACTURAT"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BillingCard({ kind, item, tab, onInvoice, onUnmark, onOpenDetails, fmtDateTime }) {
  const isInvoiced = !!item.invoiced_at;
  const title = kind === "project" ? item.name : item.name;
  const subtitle = kind === "project"
    ? (item.adresa || "")
    : item.week_label;
  const hasRate = kind === "regie" && item.billing_hourly_rate != null && item.billing_hourly_rate > 0;
  const totalHours = kind === "regie" ? (item.total_hours || 0) : 0;
  const totalAmount = kind === "regie" ? item.total_amount_chf : null;
  return (
    <div
      className={`card-flat p-3 border-2 cursor-pointer hover:shadow-md hover:border-amber-400 transition-all ${isInvoiced ? "bg-emerald-50/40 border-emerald-200" : kind === "project" ? "border-red-200 bg-red-50/30" : "border-amber-200 bg-amber-50/30"}`}
      onClick={() => onOpenDetails && onOpenDetails(kind, item)}
      data-testid={`billing-card-${kind}-${item.id}`}
      title="Click → detalii per-zi"
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">{title}</div>
          <div className="text-[11px] text-zinc-600 mt-0.5">{subtitle}</div>
          {kind === "regie" && (
            <div className="text-[10px] text-zinc-500 mt-1 inline-flex items-center gap-2 flex-wrap">
              <span><Users size={9} className="inline mr-0.5"/>{item.persons_count || 0} persoane</span>
              <span>· {item.days_worked_count || 0} zile lucrate</span>
              {item.firma && <span>· {item.firma}</span>}
            </div>
          )}
          {kind === "project" && item.dismantled_at && (
            <div className="text-[10px] text-zinc-500 mt-1">Demontat: {fmtDateTime(item.dismantled_at)}</div>
          )}
        </div>
        {kind === "regie" && (
          <div className="text-right shrink-0" data-testid={`billing-total-${item.id}`}>
            <div className="text-[9px] text-zinc-500 uppercase font-bold tracking-wider">Total ore</div>
            <div className="text-xl font-black text-amber-700 leading-none">{totalHours.toFixed(1)}h</div>
            {totalAmount != null ? (
              <div className="text-sm font-bold text-emerald-700 mt-0.5">{totalAmount.toFixed(2)} CHF</div>
            ) : hasRate ? null : (
              <div className="text-[9px] text-zinc-400 italic mt-0.5">setează tarif</div>
            )}
          </div>
        )}
      </div>
      {isInvoiced && (
        <div className="text-[10px] text-emerald-700 font-semibold bg-emerald-100 border border-emerald-300 rounded px-1.5 py-0.5 inline-block mb-1.5">
          ✓ Facturat de {item.invoiced_by} la {fmtDateTime(item.invoiced_at)}
        </div>
      )}
      <div className="flex items-center justify-end gap-1 mt-1" onClick={(e) => e.stopPropagation()}>
        {tab === "pending" ? (
          <Button onClick={onInvoice} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs" data-testid={`invoice-btn-${kind}-${item.id}`}>
            <Receipt size={11} className="mr-1"/>Facturează
          </Button>
        ) : (
          <Button onClick={onUnmark} size="sm" variant="outline" className="h-7 text-xs text-zinc-600" data-testid={`unmark-btn-${kind}-${item.id}`}>
            <RotateCcw size={11} className="mr-1"/>Anulează facturarea
          </Button>
        )}
      </div>
    </div>
  );
}

function InvoiceDetailsBlock({ dialog, fmtDate }) {
  const { kind, item, details } = dialog;
  const persons = details.persons || [];
  const pMap = Object.fromEntries(persons.map((p) => [p.id, `${p.nume || ""} ${p.prenume || ""}`.trim()]));
  const totalHours = details.total_hours || 0;
  const totalAmount = details.total_amount_chf;
  const rate = details.billing_hourly_rate;
  const DAY_N = ["Lu", "Ma", "Mi", "Jo", "Vi", "Sâ", "Du"];
  const weekDays = [];
  if (details.week_start) {
    for (let i = 0; i < 7; i++) {
      const d = new Date(new Date(details.week_start + "T00:00:00Z").getTime() + i * 86400000);
      const iso = d.toISOString().slice(0, 10);
      weekDays.push({ iso, dayName: DAY_N[i], label: `${iso.slice(8, 10)}.${iso.slice(5, 7)}` });
    }
  }
  return (
    <div className="space-y-3 text-sm">
      <div className="card-flat p-3 bg-zinc-50 space-y-1">
        <div className="font-bold text-base">{kind === "project" ? item.name : `${item.name} · ${item.week_label}`}</div>
        {kind === "project" && item.adresa && <div className="text-xs text-zinc-600">📍 {item.adresa}</div>}
        {kind === "project" && item.vorarbeiter && <div className="text-xs text-zinc-600">👷 Vorarbeiter: <strong>{item.vorarbeiter}</strong></div>}
        {kind === "regie" && item.firma && <div className="text-xs text-zinc-600">🏢 Firmă: <strong>{item.firma}</strong></div>}
      </div>
      {/* Summary financiar prominent pentru regii */}
      {kind === "regie" && (
        <div className="rounded-lg border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-orange-50 p-4" data-testid="invoice-total-summary">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-[10px] text-zinc-600 uppercase font-bold tracking-wider">Total ore</div>
              <div className="text-2xl font-black text-amber-700 mt-0.5">{totalHours.toFixed(1)}h</div>
            </div>
            <div>
              <div className="text-[10px] text-zinc-600 uppercase font-bold tracking-wider">Tarif orar</div>
              <div className="text-2xl font-black text-blue-700 mt-0.5">
                {rate != null ? `${rate} CHF` : <span className="text-zinc-400 text-base italic">nesetat</span>}
              </div>
            </div>
            <div>
              <div className="text-[10px] text-zinc-600 uppercase font-bold tracking-wider">De facturat</div>
              <div className="text-2xl font-black text-emerald-700 mt-0.5">
                {totalAmount != null ? `${totalAmount.toFixed(2)} CHF` : <span className="text-zinc-400 text-base italic">—</span>}
              </div>
            </div>
          </div>
          {rate == null && (
            <div className="mt-2 text-[11px] text-amber-900 bg-amber-100 border border-amber-300 rounded px-2 py-1">
              💡 Setează <strong>tariful orar de facturare</strong> pe această regie (în fișa regiei → billing_hourly_rate) ca să calculăm automat totalul.
            </div>
          )}
        </div>
      )}
      {/* Breakdown matrice: persoane (stânga) × zile (sus) */}
      {kind === "regie" && (details.person_matrix || []).length > 0 && (
        <div>
          <Label className="text-[10px] uppercase font-bold text-zinc-500">
            📅 Persoane × Zile — ore lucrate
          </Label>
          <div className="mt-1 border border-zinc-200 rounded overflow-x-auto">
            <table className="w-full text-xs" data-testid="invoice-person-matrix">
              <thead className="bg-zinc-100 text-zinc-600">
                <tr>
                  <th className="text-left px-2 py-1.5 font-semibold sticky left-0 bg-zinc-100">Persoana</th>
                  {weekDays.map((d) => (
                    <th key={d.iso} className="text-center px-1.5 py-1.5 font-semibold whitespace-nowrap">
                      {d.dayName}<br /><span className="font-normal tabular-nums">{d.label}</span>
                    </th>
                  ))}
                  <th className="text-right px-2 py-1.5 font-bold">Σ</th>
                </tr>
              </thead>
              <tbody>
                {details.person_matrix.map((p) => (
                  <tr key={p.person_id} className="border-t border-zinc-100 hover:bg-amber-50/30" data-testid={`matrix-row-${p.person_id}`}>
                    <td className="px-2 py-1.5 font-semibold whitespace-nowrap sticky left-0 bg-white">{p.name}</td>
                    {weekDays.map((d) => (
                      <td key={d.iso} className={`px-1.5 py-1.5 text-center tabular-nums ${p.days[d.iso] ? "font-bold text-amber-700" : "text-zinc-300"}`}>
                        {p.days[d.iso] ? `${p.days[d.iso]}h` : "—"}
                      </td>
                    ))}
                    <td className="px-2 py-1.5 text-right font-black text-amber-800 tabular-nums">{p.total_hours}h</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-zinc-300 bg-zinc-50 font-bold">
                  <td className="px-2 py-1.5 sticky left-0 bg-zinc-50">Total zi</td>
                  {weekDays.map((d) => {
                    const t = details.person_matrix.reduce((s, p) => s + (p.days[d.iso] || 0), 0);
                    return (
                      <td key={d.iso} className="px-1.5 py-1.5 text-center tabular-nums">
                        {t > 0 ? `${parseFloat(t.toFixed(2))}h` : "—"}
                      </td>
                    );
                  })}
                  <td className="px-2 py-1.5 text-right text-amber-800 tabular-nums">{totalHours.toFixed(1)}h</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Raport fizic al săptămânii */}
      {kind === "regie" && (
        <WeekReportsBlock details={details} item={item} />
      )}
      {/* Ce era de făcut */}
      {(kind === "project") && (details.project?.work_zones || details.project?.description || (details.project?.zones?.length > 0)) && (
        <div>
          <Label className="text-[10px] uppercase font-bold text-zinc-500">📐 Ce era de făcut (din ofertă)</Label>
          {details.project?.zones?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1 mb-1">
              {details.project.zones.map((z, i) => (
                <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-100 border border-zinc-300 font-semibold">{z.count}× {z.label || z.type}</span>
              ))}
            </div>
          )}
          {details.project?.work_zones && (
            <div className="text-xs whitespace-pre-wrap bg-white border border-zinc-200 rounded p-2">{details.project.work_zones}</div>
          )}
          {details.project?.description && !details.project?.work_zones && (
            <div className="text-xs whitespace-pre-wrap bg-white border border-zinc-200 rounded p-2">{details.project.description}</div>
          )}
        </div>
      )}
      {/* Cine a lucrat + când */}
      <div>
        <Label className="text-[10px] uppercase font-bold text-zinc-500">
          <FileText size={11} className="inline mr-1"/>Cine a lucrat și când ({details.days_worked_count || 0} zile)
        </Label>
        {details.schedule_entries?.length > 0 ? (
          <div className="text-xs space-y-0.5 max-h-40 overflow-y-auto bg-white border border-zinc-200 rounded p-2">
            {details.schedule_entries.map((e, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="font-mono text-zinc-500 tabular-nums">{fmtDate(e.start_date)}</span>
                <span className="flex-1">{(e.person_ids || []).map((pid) => pMap[pid] || pid).join(", ") || "—"}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-zinc-400 italic">Nicio zi înregistrată.</div>
        )}
      </div>
      {kind === "project" && details.project?.special && (
        <div className="bg-red-50 border-l-4 border-red-600 rounded p-2">
          <Label className="text-[10px] uppercase font-bold text-red-700">⚠️ Note speciale proiect</Label>
          <div className="text-xs text-red-900 whitespace-pre-wrap mt-0.5">{details.project.special}</div>
        </div>
      )}
    </div>
  );
}

function WeekReportsBlock({ details, item }) {
  const [reports, setReports] = useState(details.reports || []);
  const [uploading, setUploading] = useState(false);
  const repUrl = (rid) => `${API}/signed-reports/${rid}/file?token=${encodeURIComponent(tokenStore.get() || "")}`;
  const fmtDM = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : "");

  const doUpload = async (file) => {
    if (!file) return;
    const firstPid = details.person_matrix?.[0]?.person_id || details.persons?.[0]?.id;
    if (!firstPid) { toast.error("Nicio persoană pe această săptămână — nu pot atașa raportul"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("report_type", "wochen");
      fd.append("start_date", details.week_start);
      fd.append("end_date", details.week_end);
      fd.append("regie_id", item.regie_id || details.regie?.id);
      fd.append("person_id", firstPid);
      fd.append("note", "Încărcat de admin din Facturare");
      const { data } = await api.post("/signed-reports", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReports((prev) => [...prev, data]);
      toast.success(`Raport încărcat${data.auto_closed_tasks ? ` · ${data.auto_closed_tasks} task(uri) „raport lipsă" închise automat` : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la încărcare");
    } finally { setUploading(false); }
  };
  const upload = (e) => { const f = e.target.files?.[0]; e.target.value = ""; doUpload(f); };

  return (
    <DropZone onFiles={(files) => doUpload(files[0])} disabled={uploading} className="rounded-md" testId="invoice-week-reports">
      <Label className="text-[10px] uppercase font-bold text-zinc-500">📄 Raport fizic — verifică 1:1 cu orele</Label>
      {reports.length === 0 && (
        <div className="mt-1 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-300 rounded px-2 py-1.5" data-testid="invoice-no-report">
          ⚠ Niciun raport fizic încărcat pentru această săptămână.
        </div>
      )}
      {reports.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-3">
          {reports.map((r) => {
            const isImg = (r.content_type || "").startsWith("image/");
            return (
              <a key={r.id} href={repUrl(r.id)} target="_blank" rel="noreferrer"
                 className="block border border-zinc-300 rounded-lg overflow-hidden bg-white hover:shadow-lg transition-shadow"
                 data-testid={`invoice-report-${r.id}`} title="Deschide raportul mărit">
                {isImg ? (
                  <img src={repUrl(r.id)} alt={r.original_filename || "raport"} className="h-52 w-auto max-w-[360px] object-contain bg-zinc-100" loading="lazy" />
                ) : (
                  <div className="h-52 w-40 flex flex-col items-center justify-center bg-red-50 text-red-600 gap-1">
                    <FileText size={30} />
                    <span className="text-xs font-bold">PDF — deschide</span>
                  </div>
                )}
                <div className="px-2 py-1 border-t border-zinc-200 text-[11px]">
                  <span className="font-bold tabular-nums">{fmtDM(r.start_date)}–{fmtDM(r.end_date)}</span>
                  {r.person_name && <span className="text-zinc-500"> · pt. {r.person_name}</span>}
                </div>
              </a>
            );
          })}
        </div>
      )}
      <label className={`mt-2 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md cursor-pointer transition-colors ${reports.length === 0 ? "bg-amber-600 text-white hover:bg-amber-700" : "bg-zinc-100 text-zinc-700 border border-zinc-300 hover:bg-zinc-200"}`} data-testid="invoice-upload-report">
    📷 {uploading ? "Se încarcă…" : reports.length === 0 ? "Încarcă raportul fizic acum" : "Adaugă alt raport"} <span className="font-normal opacity-75">· sau trage fișierul aici</span>
        <input type="file" accept="image/*,.pdf" className="hidden" onChange={upload} disabled={uploading} />
      </label>
    </DropZone>
  );
}
